import {
  Component,
  OnInit,
  AfterViewInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-duna-map',
  imports: [RouterModule],
  templateUrl: './duna-map.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-map.component.scss',
})
export class DunaMapComponent implements OnInit, AfterViewInit {
  // Configuration for DUNA regions and region names
  private config = {
    // DUNA regions - these are the regions that use DUNA
    dunaRegions: [
      '4k-g',
      'nsbv-g',
      'brsj-g',
      'bsik-g',
      'ostbv-g',
      'greenland-g',
      'sojbv-g',
    ],

    // Region names for tooltips
    regionNames: {
      'brk-g': 'Bornholms Brandvæsen',
      'nobr-g': 'Nordjyllands Beredskab',
      'nvjbrand-g': 'Nordvestjyllands Brandvæsen',
      'bsik-g': 'Beredskab & Sikkerhed',
      'mjbr-g': 'Midtjysk Brand & Redning',
      'ostbv-g': 'Østjyllands Brandvæsen',
      'brmv-g': 'Brand & Redning MidtVest',
      'sojbv-g': 'Sydøstjyllands Brandvæsen',
      'vejle-g': 'Vejle Brandvæsen',
      'trekantbrand-g': 'Trekant Brand',
      'svjb-g': 'Sydvestjysk Brandvæsen',
      'sonderborg-g': 'Beredskab Sønderborg',
      'brsj-g': 'Brand & Redning Sønderjylland',
      'beredskabfyn-g': 'Beredskab Fyn',
      'helsingor-g': 'Helsingør Kommunes Beredskab',
      'gribskov-g': 'Gribskov Beredskab',
      'nsbv-g': 'Nordsjællands Brandvæsen',
      'beros-g': 'Beredskab Øst',
      'fbbr-g': 'Frederiksborg Brand & Redning',
      'taarnby-g': 'Tårnby Brandvæsen',
      'hbr-g': 'Hovedstadens Beredskab',
      '4k-g': 'Beredskab 4K',
      'roskilde-g': 'Vestsjællands Brandvæsen',
      'lejre-g': 'Lejre Brandvæsen',
      'vsbv-g': 'Vestsjællands Brandvæsen',
      'koege-g': 'Brand & Redning Køge',
      'slagelse-g': 'Slagelse Brand og Redning',
      'msbr-g': 'Midt- og Sydsjællands Brand & Redning',
      'lfbv-g': 'Lolland-Falster Brandvæsen',
      'greenland-g': 'Grønland',
      'faroe-g': 'Færøerne',
    } as { [key: string]: string },
  };

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.loadSVG();
  }

  private async loadSVG(): Promise<void> {
    try {
      const response = await fetch('assets/kort/rbis.svg');
      const svgText = await response.text();

      const svgContainer = document.getElementById('svgContainer');
      if (svgContainer) {
        svgContainer.innerHTML = svgText;

        const svg = svgContainer.querySelector('svg');
        if (svg) {
          svg.style.maxWidth = '100%';
          svg.style.height = 'auto';

          this.initializeMap(svg);
        } else {
          console.error('No SVG element found in loaded content');
          svgContainer.innerHTML =
            '<div class="loading-message text-center text-gray-600 italic p-5">Fejl: Kunne ikke indlæse SVG kort</div>';
        }
      }
    } catch (error) {
      console.error('Failed to load SVG:', error);
      const svgContainer = document.getElementById('svgContainer');
      if (svgContainer) {
        svgContainer.innerHTML =
          '<div class="loading-message text-center text-gray-600 italic p-5">Fejl: Kunne ikke indlæse kortfil</div>';
      }
    }
  }

  private initializeMap(svg: SVGElement): void {

    const tooltip = document.getElementById('tooltip');

    // Find all groups that end with "-g" (region groups)
    const regionGroups = svg.querySelectorAll('g[inkscape\\:label$="-g"]');

    regionGroups.forEach((group) => {
      const regionId = group.getAttribute('inkscape:label');
      if (!regionId) return;

      // Find all paths within this group
      const paths = group.querySelectorAll('path');

      paths.forEach((path) => {
        // Apply default styling - force override any existing styles
        (path as unknown as HTMLElement).style.setProperty(
          'fill',
          '#e0e0e0',
          'important'
        );
        (path as unknown as HTMLElement).style.setProperty(
          'fill-opacity',
          '1',
          'important'
        );
        (path as unknown as HTMLElement).style.setProperty(
          'stroke',
          '#999',
          'important'
        );
        (path as unknown as HTMLElement).style.setProperty(
          'stroke-width',
          '1',
          'important'
        );
        (path as unknown as HTMLElement).style.transition =
          'fill 0.3s ease, stroke-width 0.3s ease';
        (path as unknown as HTMLElement).style.cursor = 'pointer';

        // Special handling for faroe group - ensure it has a border
        if (regionId === 'faroe-g') {
          (path as unknown as HTMLElement).style.setProperty(
            'stroke',
            '#000',
            'important'
          );
          (path as unknown as HTMLElement).style.setProperty(
            'stroke-width',
            '1',
            'important'
          );
        }

        // Apply DUNA styling if this region is in the DUNA list
        if (this.config.dunaRegions.includes(regionId)) {
          (path as unknown as HTMLElement).style.setProperty(
            'fill',
            '#274273',
            'important'
          );
          (path as unknown as HTMLElement).style.setProperty(
            'fill-opacity',
            '1',
            'important'
          );
        }

        // Add hover effects
        path.addEventListener('mouseenter', (e) => {
          // Highlight entire group on hover
          const allPathsInGroup = group.querySelectorAll('path');
          allPathsInGroup.forEach((groupPath) => {
            if (this.config.dunaRegions.includes(regionId)) {
              (groupPath as unknown as HTMLElement).style.setProperty(
                'fill',
                '#244277',
                'important'
              ); // Darker blue on hover
            } else {
              (groupPath as unknown as HTMLElement).style.setProperty(
                'fill',
                '#c0c0c0',
                'important'
              ); // Darker gray on hover
            }
            (groupPath as unknown as HTMLElement).style.setProperty(
              'stroke-width',
              '2',
              'important'
            );
            (groupPath as unknown as HTMLElement).style.setProperty(
              'stroke',
              '#666',
              'important'
            );
          });

          const regionName = this.config.regionNames[regionId] || regionId;
          this.showTooltip(e as MouseEvent, regionName);
        });

        path.addEventListener('mousemove', (e) => {
          this.updateTooltipPosition(e as MouseEvent);
        });

        path.addEventListener('mouseleave', () => {
          // Reset all paths in the group
          const allPathsInGroup = group.querySelectorAll('path');
          allPathsInGroup.forEach((groupPath) => {
            if (this.config.dunaRegions.includes(regionId)) {
              (groupPath as unknown as HTMLElement).style.setProperty(
                'fill',
                '#274273',
                'important'
              );
            } else {
              (groupPath as unknown as HTMLElement).style.setProperty(
                'fill',
                '#e0e0e0',
                'important'
              );
            }
            (groupPath as unknown as HTMLElement).style.setProperty(
              'stroke-width',
              '1',
              'important'
            );

            // Special border handling for faroe group
            if (regionId === 'faroe-g') {
              (groupPath as unknown as HTMLElement).style.setProperty(
                'stroke',
                '#000',
                'important'
              );
            } else {
              (groupPath as unknown as HTMLElement).style.setProperty(
                'stroke',
                '#999',
                'important'
              );
            }
          });

          this.hideTooltip();
        });
      });
    });

  }

  private showTooltip(event: MouseEvent, text: string): void {
    const tooltip = document.getElementById('tooltip');
    if (tooltip) {
      tooltip.textContent = text;
      tooltip.style.opacity = '1';
      this.updateTooltipPosition(event);
    }
  }

  private updateTooltipPosition(event: MouseEvent): void {
    const tooltip = document.getElementById('tooltip');
    if (!tooltip) return;

    const rect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = event.pageX - rect.width / 2;
    let top = event.pageY - rect.height - 10;

    // Prevent tooltip from going off-screen
    if (left < 10) left = 10;
    if (left + rect.width > viewportWidth - 10)
      left = viewportWidth - rect.width - 10;
    if (top < 10) top = event.pageY + 10;

    tooltip.style.left = left + 'px';
    tooltip.style.top = top + 'px';
  }

  private hideTooltip(): void {
    const tooltip = document.getElementById('tooltip');
    if (tooltip) {
      tooltip.style.opacity = '0';
    }
  }
}
